import React, { useState, useEffect, useCallback } from 'react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { 
  BarChart3, TrendingUp, Users, Zap, Database, 
  Activity, Server, Clock, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { MetricCard } from '../../components/MetricCard';

/**
 * Usage Analytics - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - NO MOCK DATA: Real /api/platform-admin/tenants/usage
 * - NULL SAFETY: Handles missing data
 * - PROGRESSIVE COMPLEXITY: Rich analytics UI
 */

interface TenantUsage {
  tenant_id: string;
  tenant_name: string;
  period_days: number;
  total_alerts: number;
  total_users: number;
  total_connectors: number;
  api_calls: number;
  storage_mb: number;
  ml_predictions: number;
  avg_response_time_ms: number;
}

export const UsageAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantUsage[]>([]);
  const [periodDays, setPeriodDays] = useState(30);
  const [sortBy, setSortBy] = useState<'alerts' | 'users' | 'name'>('alerts');

  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${getBaseUrl()}/api/platform-admin/tenants/usage?days=${periodDays}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch usage analytics');
      
      const data = await response.json();
      setTenants(data.tenants || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching usage analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const sortedTenants = [...tenants].sort((a, b) => {
    switch (sortBy) {
      case 'alerts':
        return b.total_alerts - a.total_alerts;
      case 'users':
        return b.total_users - a.total_users;
      case 'name':
        return a.tenant_name.localeCompare(b.tenant_name);
      default:
        return 0;
    }
  });

  const aggregateStats = {
    totalAlerts: tenants.reduce((sum, t) => sum + t.total_alerts, 0),
    totalUsers: tenants.reduce((sum, t) => sum + t.total_users, 0),
    totalConnectors: tenants.reduce((sum, t) => sum + t.total_connectors, 0),
    totalApiCalls: tenants.reduce((sum, t) => sum + t.api_calls, 0),
    totalStorage: tenants.reduce((sum, t) => sum + t.storage_mb, 0),
    avgAlerts: tenants.length > 0 ? Math.round(tenants.reduce((sum, t) => sum + t.total_alerts, 0) / tenants.length) : 0,
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatStorage = (mb: number) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading usage analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Usage Data</h3>
          <p className="text-red-400/80">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">Usage Analytics</h1>
          <p className="text-gray-400">Track resource consumption and activity across all tenants</p>
        </div>
        
        {/* Period Selector */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Aggregate Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          title="Total Alerts"
          value={formatNumber(aggregateStats.totalAlerts)}
          icon={Zap}
          status="info"
          description={`Avg: ${formatNumber(aggregateStats.avgAlerts)}/tenant`}
        />
        
        <MetricCard
          title="Total Users"
          value={formatNumber(aggregateStats.totalUsers)}
          icon={Users}
          status="success"
          description="Across all tenants"
        />
        
        <MetricCard
          title="Connectors"
          value={aggregateStats.totalConnectors}
          icon={Server}
          status="info"
          description="Active integrations"
        />
        
        <MetricCard
          title="API Calls"
          value={formatNumber(aggregateStats.totalApiCalls)}
          icon={Activity}
          status="info"
          description="Total requests"
        />
        
        <MetricCard
          title="Storage"
          value={formatStorage(aggregateStats.totalStorage)}
          icon={Database}
          status="info"
          description="Total consumed"
        />
        
        <MetricCard
          title="Tenants"
          value={tenants.length}
          icon={BarChart3}
          status="info"
          description="Being monitored"
        />
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400">Sort by:</span>
        {(['alerts', 'users', 'name'] as const).map((sort) => (
          <button
            key={sort}
            onClick={() => setSortBy(sort)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              sortBy === sort
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {sort.charAt(0).toUpperCase() + sort.slice(1)}
          </button>
        ))}
      </div>

      {/* Tenant Usage Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="h-3.5 w-3.5" />
                    Alerts
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Users
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Server className="h-3.5 w-3.5" />
                    Connectors
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Activity className="h-3.5 w-3.5" />
                    API Calls
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Database className="h-3.5 w-3.5" />
                    Storage
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Avg Response
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedTenants.map((tenant, index) => {
                const isTopTenant = index < 3;
                return (
                  <tr 
                    key={tenant.tenant_id} 
                    className={`hover:bg-gray-700/50 transition-colors ${isTopTenant ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {isTopTenant && (
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-semibold">
                            {index + 1}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-white">{tenant.tenant_name}</div>
                          <div className="text-xs text-gray-500">{periodDays} days</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">{formatNumber(tenant.total_alerts)}</div>
                      <div className="text-xs text-gray-500">{Math.round(tenant.total_alerts / periodDays)}/day</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">{tenant.total_users}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">{tenant.total_connectors}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">
                        {tenant.api_calls > 0 ? formatNumber(tenant.api_calls) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">
                        {tenant.storage_mb > 0 ? formatStorage(tenant.storage_mb) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-semibold text-white">
                        {tenant.avg_response_time_ms > 0 ? `${tenant.avg_response_time_ms}ms` : '-'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedTenants.length === 0 && (
          <div className="p-12 text-center">
            <BarChart3 className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Usage Data</h3>
            <p className="text-gray-400">No usage data available for the selected period</p>
          </div>
        )}
      </div>

      {/* Top Performers */}
      {sortedTenants.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-lg p-6 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">Most Active</h3>
            </div>
            <div className="space-y-3">
              {sortedTenants.slice(0, 3).map((tenant, idx) => (
                <div key={tenant.tenant_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-blue-400">#{idx + 1}</span>
                    <span className="text-sm text-white truncate">{tenant.tenant_name}</span>
                  </div>
                  <span className="text-sm font-semibold text-blue-400">
                    {formatNumber(tenant.total_alerts)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-lg p-6 border border-green-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-green-400" />
              <h3 className="text-sm font-semibold text-white">Largest Teams</h3>
            </div>
            <div className="space-y-3">
              {[...sortedTenants].sort((a, b) => b.total_users - a.total_users).slice(0, 3).map((tenant, idx) => (
                <div key={tenant.tenant_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-green-400">#{idx + 1}</span>
                    <span className="text-sm text-white truncate">{tenant.tenant_name}</span>
                  </div>
                  <span className="text-sm font-semibold text-green-400">
                    {tenant.total_users} users
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-lg p-6 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Server className="h-5 w-5 text-purple-400" />
              <h3 className="text-sm font-semibold text-white">Most Integrated</h3>
            </div>
            <div className="space-y-3">
              {[...sortedTenants].sort((a, b) => b.total_connectors - a.total_connectors).slice(0, 3).map((tenant, idx) => (
                <div key={tenant.tenant_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-purple-400">#{idx + 1}</span>
                    <span className="text-sm text-white truncate">{tenant.tenant_name}</span>
                  </div>
                  <span className="text-sm font-semibold text-purple-400">
                    {tenant.total_connectors} sources
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageAnalytics;
